import React, { useState, useEffect, useContext } from "react";
import ListLocationTypes from "./list-location-types";
import { checkNoSpecialChars } from "./system-icons";
import getSvgImgSrc from "../../core/utils/getSvgImgSrc";
import MultipleChoice from "../../core/ui/multiple-choice";
import SessionContext from "SessionContext";
import { EditableRadiusInput } from "./company-selector";
import LocationRebuildNotice from "../../core/ui/location-rebuild-notice";
import isEqual from "lodash.isequal";
import UnsavedChanges, { UnsavedChangesManual } from "../../partials/unsaved-changes";

function LocationTypes(props) {
  const session = useContext(SessionContext);
  const {
    toggleShowSystemLocationTypes,
    locationTypeList,
    deleteLocationType,
    editLocationType,
    editingSysLocationType,
    newSysLocationTypeToggle,
    creatingNewSysLocationType,
    setLocationTypeName,
    setLocationTypeColor,
    setLocationTypeDefaultRadius,
    onChangeLocationTypeIcon,
    svgIconList,
    onCancel,
    newSystemLocationType,
    sysLocationTypeToEdit,
    setSysLocationTypeToEditName,
    setSysLocationTypeToEditColor,
    onChangeSysLocationTypeToEditIcon,
    updateSysLocationTypeToEdit,
  } = props;

  const [createNewDetails, setCreateNewDetails] = useState({
    name: "",
    color: "#929292",
    radius: 3,
    icon: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    color: "",
    radius: "",
  });
  const [disableCreateNew, setDisableCreateNew] = useState(true);
  const [presetSystemIcon, setPresetSystemIcon] = useState("");
  const [defaultRadius, setDefaultRadius] = useState("5");
  const [radiusChoices, setRadiusChoices] = useState([]);
  const [editChoices, setEditChoices] = useState(false);
  const [selectedCompanyRadiusInfo, setSelectedCompanyRadiusInfo] = useState(null);
  const [hasUpdates, setHasUpdates] = useState(false);
  const successLabel = "System Default Radius Updated.";
  const [disableUpdate, setDisableUpdate] = useState(true);
  const [originalLocationTypeToEdit, setOriginalLocationTypeToEdit] = useState();
  const [disableUpdateLocationType, setDisableUpdateLocationType] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showManualModel, setShowManualModel] = useState(false);
  const [showRebuildNotice, setShowRebuildNotice] = useState(false);

  const onCreateNewName = (name, action = "create") => {
    switch (action) {
      case "edit":
        if (checkNoSpecialChars(name.target.value)) {
          setErrors({ ...errors, name: "" });
          setSysLocationTypeToEditName(name);
        } else {
          setErrors({
            ...errors,
            name: "*Special characters are not allowed.",
          });
        }
        break;

      default:
        if (checkNoSpecialChars(name.target.value)) {
          setErrors({ ...errors, name: "" });
          setCreateNewDetails({ ...createNewDetails, name: name.target.value });
          setLocationTypeName(name);
        } else {
          setErrors({
            ...errors,
            name: "*Special characters are not allowed.",
          });
        }
        break;
    }
  };

  const onCreateNewColor = (color, action = "create") => {
    switch (action) {
      case "edit":
        setSysLocationTypeToEditColor(color);
        break;

      default:
        setCreateNewDetails({ ...createNewDetails, color: color.target.value });
        setLocationTypeColor(color);
        break;
    }
  };

  useEffect(() => {
    setLocationTypeColor({ target: { value: "#929292" } });
    setLocationTypeDefaultRadius({ target: { value: "3" } });
    if (sysLocationTypeToEdit.svg_icon_id && svgIconList) {
      onSelectIcon(sysLocationTypeToEdit.svg_icon_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sysLocationTypeToEdit, svgIconList]);

  const onCreateNewIcon = (icon, action = "create") => {
    switch (action) {
      case "edit":
        onSelectIcon(icon.target.value);
        onChangeSysLocationTypeToEditIcon(icon);
        break;

      default:
        onSelectIcon(icon.target.value);
        onChangeLocationTypeIcon(icon);
        break;
    }
  };

  const onSelectIcon = (i) => {
    let iconSvg = "";
    svgIconList.forEach((e) => {
      if (e.id === Number(i)) {
        iconSvg = e.svg;
        setPresetSystemIcon(e.id);
      }
    });
    setCreateNewDetails({ ...createNewDetails, icon: iconSvg });
  };

  const onSubmitCreateNew = () => {
    newSystemLocationType();
    handleCancel();
  };

  const handleStartNewLocationType = () => {
    // reset previous state
    setCreateNewDetails({ ...createNewDetails, name: "", icon: "" });
    newSysLocationTypeToggle();
  }

  const handleCancel = () => {
    setCreateNewDetails({
      name: "",
      color: "#929292",
      radius: 3,
      icon: "",
    });
    setErrors({
      name: "",
      color: "",
      radius: "",
    });
    onCancel();
  };

  const onBack = () => {
    handleCancel();
    toggleShowSystemLocationTypes();
  };

  useEffect(() => {
    const handleDisableCreateNew = () => {
      if (createNewDetails.name === "" || createNewDetails.icon === "") {
        setDisableCreateNew(true);
      } else {
        setDisableCreateNew(false);
      }
    };

    handleDisableCreateNew();
  }, [createNewDetails.name, createNewDetails.icon]);

  const checkBeforeLeave = () => {
    if(hasUnsavedChanges) {
      setShowManualModel(true);
    } else {
      onBack();
    }
  }

  const handleUnsavedLeave = () => {
    setShowManualModel(false);
    onBack();
  }

  const handleUnsavedCancel = () => {
    setShowManualModel(false);
  }

  useEffect(() => {
    if (!disableCreateNew || !disableUpdateLocationType || !disableUpdate) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [disableUpdateLocationType, disableCreateNew, disableUpdate])

  useEffect(() => {
    function loadDefaultRadius() {
      session
        .query("location/system-radius-defaults")
        .then((response) => {
          setSelectedCompanyRadiusInfo(response);
          setRadiusChoices([
            response.radius_1,
            response.radius_2,
            response.radius_3,
            response.radius_4,
            response.radius_5,
          ])
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        });
    }
    
    loadDefaultRadius();
    // eslint-disable-next-line
  }, [])

  /**
   * Editing Functions
   */

  const onSubmitUpdate = () => {
    updateSysLocationTypeToEdit();
    handleCancel();
  };

  const onEditRadius = (value) => {
    setDefaultRadius(value);
  }

  // load radius options and default value for the company
  function loadSystemRadius() {
    session
      .query("location/system-radius-defaults")
      .then((response) => {
        setSelectedCompanyRadiusInfo(response);
        setRadiusChoices([
          response.radius_1,
          response.radius_2,
          response.radius_3,
          response.radius_4,
          response.radius_5,
        ]);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  const onSaveNewRadius = () => {
    let newChoice = radiusChoices.indexOf(defaultRadius) + 1;
    let formatData = {
      ...selectedCompanyRadiusInfo,
      default_radius_selection: newChoice,
    }

    if(newChoice) {
      session
        .command(
          "location/persist-default-radius-setting",
          formatData,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          setHasUpdates(false);
          setDisableUpdate(true);
          setShowRebuildNotice(true);
          loadSystemRadius();
        })
        .catch((error) => {
          console.log(JSON.stringify(error));
        });
    }
  }

  const onUpdateOptions = (newOptions) => {
    setSelectedCompanyRadiusInfo({
      ...selectedCompanyRadiusInfo,
      radius_1: newOptions.r_1,
      radius_2: newOptions.r_2,
      radius_3: newOptions.r_3,
      radius_4: newOptions.r_4,
      radius_5: newOptions.r_5,
    });
    setHasUpdates(true);
  }

  const handleRadiusOptionsChanged = (_changed) => {
    setDisableUpdate(_changed)
  }

  const onToggleDefaultOptions = () => {
    setDefaultRadius(
      radiusChoices[selectedCompanyRadiusInfo.default_radius_selection - 1]
    );
    setEditChoices(!editChoices);
    setDisableUpdate(true);
  }

  // call api after the values have changed
  useEffect(() => {
    if(hasUpdates) {
      onSaveNewRadius();
    }
    // eslint-disable-next-line
  }, [hasUpdates])

  useEffect(() => {
    if(selectedCompanyRadiusInfo) {
      setTimeout(() => {
        setDefaultRadius(
          radiusChoices[selectedCompanyRadiusInfo.default_radius_selection - 1]
        );
      }, 300);
    }
    // eslint-disable-next-line
  }, [selectedCompanyRadiusInfo, radiusChoices])

  useEffect(() => {
    if(originalLocationTypeToEdit) {
      // the html selector saves it as string, api sends a number, make both string to compare
      let _original = {...originalLocationTypeToEdit, svg_icon_id: originalLocationTypeToEdit.svg_icon_id.toString()};
      let _new_changes = {...sysLocationTypeToEdit, svg_icon_id: sysLocationTypeToEdit.svg_icon_id.toString()};
      let hasChanges = isEqual(_original, _new_changes);
      setDisableUpdateLocationType(hasChanges);
    } else {
      setDisableUpdateLocationType(true);
    }
  // eslint-disable-next-line
  }, [sysLocationTypeToEdit, originalLocationTypeToEdit])

  useEffect(() => {
    if(Object.keys(sysLocationTypeToEdit).length) {
      setOriginalLocationTypeToEdit({...sysLocationTypeToEdit});
    }
  // eslint-disable-next-line
  }, [sysLocationTypeToEdit.id])

  useEffect(() => {
    let stored_radius = 0;
    if(selectedCompanyRadiusInfo) {
      stored_radius = radiusChoices[selectedCompanyRadiusInfo.default_radius_selection - 1];
    }
    if(defaultRadius !== stored_radius) {
      setDisableUpdate(false);
    } else {
      setDisableUpdate(true);
    }
  // eslint-disable-next-line
  }, [defaultRadius, selectedCompanyRadiusInfo])

  return (
    <>
      <div className="flex-inline mb-4">
        <button type="button" className="btn-back-icons" onClick={checkBeforeLeave}>
          <i className="bi bi-chevron-left"></i> Back
        </button>
        <h3>System Location Types</h3>
      </div>

      <div className="icon-list-section">
        <div>

        <div className="m-3 radius-editor">
          <div>
            <strong>System Default Radius (miles)</strong>
            {editChoices ? <>
              <button className="radius-editor-btn" onClick={onToggleDefaultOptions}>
                <i className="bi bi-x-circle cancel-icon" /> Cancel
              </button>
              </> : 
              <button className="radius-editor-btn" onClick={onToggleDefaultOptions}>
                <i className="bi bi-pencil" /> <span className="edit-options-hover">Edit Options</span>
              </button>
            }
          </div>
          {
            editChoices ? 
              <EditableRadiusInput 
                radiusChoices={radiusChoices} 
                onUpdateOptions={onUpdateOptions}
                onClose={onToggleDefaultOptions}
                radiusOptionsChanged={handleRadiusOptionsChanged}
              /> 
            : (<>
              <div className="radius-choices">
                <span className="radius-label">Select a Radius:</span>
                {radiusChoices.map((choice) => 
                  <MultipleChoice 
                    key={choice}
                    value={choice} 
                    selected={defaultRadius} 
                    clicked={onEditRadius}
                  />
                )}
              </div>
              <button 
                type="button" 
                className="btn-back-icons radius-btn" 
                onClick={onSaveNewRadius}
                disabled={disableUpdate}
              >
                Update Radius
              </button>
              </>)              
          }
          
        </div>

          <ListLocationTypes
            locationTypeList={locationTypeList}
            deleteLocationType={deleteLocationType}
            editLocationType={editLocationType}
          />
        </div>

        <div className="right-panel">
          {!editingSysLocationType && (
            <button
              onClick={handleStartNewLocationType}
              disabled={creatingNewSysLocationType}
            >
              Create New Location Type
            </button>
          )}
          {creatingNewSysLocationType && !editingSysLocationType && (
            <div className="create-new-section new-system-icon-sm">
              <div>
                <span className="label">Name:</span>
                <input
                  type="text"
                  value={createNewDetails.name}
                  onChange={(e) => onCreateNewName(e)}
                />
                <span className="error-msg">{errors.name}</span>
              </div>

              <div className="color-picker">
                <span className="label">Color:</span>
                <input
                  type="color"
                  value={createNewDetails.color}
                  onChange={(e) => onCreateNewColor(e)}
                />
                <span className="display-hex-color">
                  {createNewDetails.color}
                </span>
                <span className="error-msg">{errors.color}</span>
              </div>

              <div>
                <span className="label">System Icon:</span>
                <select
                  onChange={(e) => onCreateNewIcon(e)}
                  className="select-icon"
                >
                  <option value="">Select an Icon</option>
                  {svgIconList.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <span>
                  {createNewDetails.icon.length > 5 && (
                    <img
                      height="25"
                      width="25"
                      src={getSvgImgSrc(createNewDetails.icon)}
                      alt="icon"
                    />
                  )}
                </span>
              </div>

              <div className="edit-btn-section">
                <button onClick={handleCancel}>Cancel</button>
                <button className="promote" onClick={onSubmitCreateNew} disabled={disableCreateNew}>
                  Save
                </button>
              </div>
            </div>
          )}

          {editingSysLocationType && (
            <div className="create-new-section new-system-icon-sm">
              <div>
                <span className="label">Edit Name:</span>
                <input
                  type="text"
                  value={sysLocationTypeToEdit.name}
                  onChange={(e) => onCreateNewName(e, "edit")}
                />
                <span className="error-msg">{errors.name}</span>
              </div>

              <div className="color-picker">
                <span className="label">Edit Color:</span>
                <input
                  type="color"
                  value={sysLocationTypeToEdit.color}
                  onChange={(e) => onCreateNewColor(e, "edit")}
                />
                <span className="display-hex-color">
                  {sysLocationTypeToEdit.color}
                </span>
                <span className="error-msg">{errors.color}</span>
              </div>

              <div>
                <span className="label">Edit System Icon:</span>
                <select
                  onChange={(e) => onCreateNewIcon(e, "edit")}
                  value={presetSystemIcon}
                  className="select-icon"
                >
                  {svgIconList.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <span>
                  {createNewDetails.icon.length > 5 && (
                    <img
                      height="25"
                      width="25"
                      src={getSvgImgSrc(createNewDetails.icon)}
                      alt="icon"
                    />
                  )}
                </span>
              </div>
              <div className="edit-btn-section">
                <button onClick={handleCancel}>Cancel</button>
                <button className="promote" onClick={onSubmitUpdate} disabled={disableUpdateLocationType}>Update</button>
              </div>
            </div>
          )}
        </div>
        <LocationRebuildNotice show={showRebuildNotice} label={successLabel} onClose={() => setShowRebuildNotice(false)} />
        <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
        <UnsavedChangesManual showModal={showManualModel} confirmLeave={handleUnsavedLeave} cancelLeave={handleUnsavedCancel} />
      </div>
    </>
  );
}

export default LocationTypes;
